import { useOrgId } from 'helpers'
import { DependencyList, EffectCallback, useEffect, useRef, useState } from 'react'
import { NavigateOptions, useNavigate, useSearchParams } from 'react-router-dom'
import { URLSearchParams } from 'url'

export const useEffectNoInitial = (effect: EffectCallback, deps?: DependencyList) => {
    const initialRender = useRef(true)

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false
            return
        }

        effect()
    }, deps)
}

export const getAllSearchParams = (searchParams: URLSearchParams) => {
    var params = {} as any
    // @ts-ignore
    for (const entry of searchParams.entries()) {
        //console.log(entry[0], entry[1])
        params[entry[0]] = entry[1]
    }

    return params
}

export const useSearchParamState = <T extends string | number | undefined>(
    key: string,
    defaultValue: T,
): [T, (val: T) => void] => {
    const [searchParams, setSearchParams] = useSearchParams()

    const searchParam = searchParams.get(key) ?? defaultValue
    const value = typeof defaultValue == 'number' ? (parseInt(searchParam as any) as T) : (searchParam as T)

    const setValue = (newValue: T) => {
        if (newValue == undefined) {
            searchParams.delete(key)
        } else {
            searchParams.set(key, '' + newValue)
        }
        setSearchParams(searchParams, { replace: true })
    }

    return [value, setValue]
}


export const useSessionStorageState = <T,>(
    key: string,
    defaultValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
    const sessionKey = `sessionState_${key}`

    const getInitialValue = (): T => {
        const sessionValue = sessionStorage.getItem(sessionKey)
        if (sessionValue) {
            try {
                return JSON.parse(sessionValue)
            } catch {
                return defaultValue
            }
        }
        return defaultValue
    }

    const [value, setValueState] = useState<T>(getInitialValue)

    const setValue: React.Dispatch<React.SetStateAction<T>> = (newValue) => {
        setValueState((prevValue) => {
            // Resolve the new value (could be a value or an updater function)
            const resolvedValue = newValue instanceof Function ? newValue(prevValue) : newValue

            const isEmpty =
                resolvedValue == undefined ||
                (Array.isArray(resolvedValue) && resolvedValue.length === 0) ||
                (typeof resolvedValue === 'object' && Object.keys(resolvedValue as any).length === 0)

            if (isEmpty) {
                sessionStorage.removeItem(sessionKey)
                return defaultValue
            } else {
                sessionStorage.setItem(sessionKey, JSON.stringify(resolvedValue))
                return resolvedValue
            }
        })
    }

    return [value, setValue]
}

export function useOrgNavigate() {
    const orgId = useOrgId()
    const navigate = useNavigate()

    return (path: string, options?: NavigateOptions) => navigate(`/${orgId}${path}`, options)
}
